/**
 * laboun-mode — for-fun „Labouň mód": po zapnutí po obrazovce létají mouchy
 * (SVG z `apps/web/public/flies/`), které jdou sklepnout kliknutím. Vlastní efekt
 * vykresluje `LabounModeController`; tento modul drží jen config + opt-in stav.
 *
 * Vzor 1:1 podle `drunk-mode.ts` (config v `content_pages` key `laboun-mode`,
 * per-user opt-in v localStorage, vanilla store + `useSyncExternalStore` hook).
 *
 * Režimy:
 *   - off    — vypnuto (default)
 *   - optIn  — admin povolil; každý návštěvník si zapne sám přepínačem v hlavičce
 */

export type LabounMode = "off" | "optIn";

export interface LabounConfig {
  mode: LabounMode;
  /** Počet much na obrazovce (1..LABOUN_MAX_FLIES). */
  flyCount: number;
  /** Po sklepnutí se moucha po chvíli znovu objeví (nekonečná zábava). */
  respawn: boolean;
}

export const LABOUN_MAX_FLIES = 30;
export const DEFAULT_LABOUN_CONFIG: LabounConfig = { mode: "off", flyCount: 8, respawn: true };

/** localStorage klíč per-user opt-in přepínače. */
export const LABOUN_OPTIN_KEY = "bw:laboun";
/** Custom event — LabounToggle ho vyšle při změně opt-in, controller přepočítá. */
export const LABOUN_CHANGED_EVENT = "bw:laboun-changed";

/** URL 12 SVG much (název má mezeru → encodeURI). Servírují se ze `public/flies`. */
export const FLY_ASSETS: readonly string[] = Array.from({ length: 12 }, (_, i) =>
  encodeURI(`/flies/SVG ${i + 1}.svg`),
);

function clampCount(n: unknown): number {
  const v = typeof n === "number" && Number.isFinite(n) ? Math.round(n) : DEFAULT_LABOUN_CONFIG.flyCount;
  return Math.max(1, Math.min(LABOUN_MAX_FLIES, v));
}

/** Bezpečně rozparsuje JSON z content_pages na LabounConfig (s defaulty). */
export function parseLabounConfig(json: string): LabounConfig {
  try {
    const raw = JSON.parse(json) as Partial<LabounConfig>;
    const mode: LabounMode = raw.mode === "optIn" ? "optIn" : "off";
    const flyCount = clampCount(raw.flyCount);
    const respawn = typeof raw.respawn === "boolean" ? raw.respawn : DEFAULT_LABOUN_CONFIG.respawn;
    return { mode, flyCount, respawn };
  } catch {
    return DEFAULT_LABOUN_CONFIG;
  }
}

// ─── Config store (vanilla) — sdílený mezi controllerem a toggle/admin ─────────

let _config: LabounConfig = DEFAULT_LABOUN_CONFIG;
const _subs = new Set<() => void>();

export function getLabounConfig(): LabounConfig {
  return _config;
}

export function setLabounConfig(c: LabounConfig): void {
  _config = c;
  _subs.forEach((f) => f());
}

export function subscribeLabounConfig(cb: () => void): () => void {
  _subs.add(cb);
  return () => {
    _subs.delete(cb);
  };
}

/** Načte config z content_pages (`laboun-mode`). 404 / chyba → ponechá default (off). */
export async function loadLabounConfig(): Promise<void> {
  try {
    const res = await fetch("/api/content/laboun-mode", { headers: { Accept: "application/json" } });
    if (!res.ok) return;
    const data = (await res.json()) as { html?: string };
    if (data.html) setLabounConfig(parseLabounConfig(data.html));
  } catch {
    // ponech default
  }
}

/** Per-user opt-in přepnut? (relevantní jen v režimu optIn) */
export function isLabounOptIn(): boolean {
  try {
    return localStorage.getItem(LABOUN_OPTIN_KEY) === "1";
  } catch {
    return false;
  }
}

// ─── Počítadlo sklepnutých much ────────────────────────────────────────────────
//
// `_swatCount` = počet za tuto session (zobrazí se v hlavičce u ikony).
// `_pending`   = dosud NEodeslaný přírůstek pro server. Controller ho periodicky
//   (a při skrytí/odchodu stránky) flushne na POST /players/me/laboun-swats jako
//   deltu — kliknutí se tedy stackují na klientu, ne odesílá se každé zvlášť.
//   Perzistuje se jen u přihlášených; nepřihlášeným pending jen narůstá (zahodí
//   se odchodem) — nemáme kam ho uložit.

let _swatCount = 0;
let _pending = 0;
const _swatSubs = new Set<() => void>();

export function getLabounSwatCount(): number {
  return _swatCount;
}

/** Zvýší počítadlo sklepnutých much (volá controller při kliknutí na mouchu). */
export function incLabounSwatCount(): void {
  _swatCount += 1;
  _pending += 1;
  _swatSubs.forEach((f) => f());
}

export function subscribeLabounSwatCount(cb: () => void): () => void {
  _swatSubs.add(cb);
  return () => {
    _swatSubs.delete(cb);
  };
}

/** Kolik kliknutí čeká na odeslání serveru. */
export function getPendingLabounSwats(): number {
  return _pending;
}

/** Vyzvedne (a vynuluje) nasbíraný přírůstek pro odeslání serveru. */
export function takePendingLabounSwats(): number {
  const n = _pending;
  _pending = 0;
  return n;
}

/** Vrátí přírůstek zpět do fronty (když odeslání selhalo). */
export function restorePendingLabounSwats(n: number): void {
  if (n > 0) _pending += n;
}
